import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TreePine, Heart, Sun, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function NatureTherapyGuide() {
  const navigate = useNavigate();

  const benefits = [
    { stat: "28%", benefit: "reduction in cortisol" },
    { stat: "20 min", benefit: "is all it takes to see effects" },
    { stat: "50%", benefit: "improvement in mood" },
    { stat: "12%", benefit: "lower blood pressure" }
  ];

  const activities = [
    { activity: "Sit and Observe", desc: "Find a spot to sit quietly. Watch clouds, birds, leaves moving. Just observe without doing.", time: "10-20 min" },
    { activity: "Grounding Walk", desc: "Walk slowly, feeling each footstep. Notice textures underfoot, sounds around you.", time: "15-30 min" },
    { activity: "Cloud Watching", desc: "Lie on grass and watch clouds. Let your mind wander freely.", time: "10-15 min" },
    { activity: "Nature Journaling", desc: "Sketch or describe what you see. No artistic skill needed.", time: "15-20 min" },
    { activity: "Barefoot Connection", desc: "If possible, take shoes off and feel grass or earth (grounding).", time: "5-10 min" }
  ];

  const findNature = [
    "Local park or garden",
    "Tree-lined neighborhood streets",
    "Your own backyard or balcony",
    "A quiet bench near water",
    "Community garden",
    "Nature preserve or hiking trail"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
            Week 6 • Day 5 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Nature Therapy
          </h1>
          <p className="text-xl text-secondary-600">Let Nature Heal Your Stress</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <TreePine className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Practice</h3>
              <p className="text-green-100 text-lg">
                Spend 30 minutes in nature—a park, garden, or even your backyard. Leave your phone behind. 
                Sit and observe: watch clouds, listen to birds, feel the breeze. Just be present.
              </p>
            </div>
          </div>
        </section>

        {/* Science */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">The Science of Nature Therapy</h2>
          <p className="text-secondary-700 mb-6">
            "Shinrin-yoku" (forest bathing) has been extensively studied. Even brief nature exposure 
            triggers measurable stress reduction and immune system benefits.
          </p>
          <div className="grid md:grid-cols-4 gap-4">
            {benefits.map((item, i) => (
              <div key={i} className="text-center p-4 bg-green-50 rounded-xl">
                <p className="text-2xl font-bold text-green-600">{item.stat}</p>
                <p className="text-sm text-secondary-600">{item.benefit}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Activities */}
        <section className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Nature Activities</h2>
          <div className="space-y-3">
            {activities.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <h3 className="font-bold text-green-700 md:w-1/4">{item.activity}</h3>
                <p className="text-sm text-secondary-600 md:flex-1">{item.desc}</p>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full w-fit">{item.time}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Find Nature */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Where to Find Nature</h2>
          <p className="text-secondary-600 mb-4">You don't need a forest—even small doses of nature help:</p>
          <div className="grid md:grid-cols-2 gap-3">
            {findNature.map((place, i) => (
              <div key={i} className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-secondary-700">
                <TreePine className="w-4 h-4 text-green-500 flex-shrink-0" />
                {place}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default NatureTherapyGuide;
